import { createRouter, createWebHistory } from 'vue-router'
import LoginView from '@/views/LoginView.vue'
import {
  ensureAuthReady,
  hasAuthenticatedSession,
  hasPendingVerification,
  hasRole,
  mustChangePassword,
} from '@/composables/useAuth'
import { logger } from '@/utils/logger'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'login',
      component: LoginView,
      meta: { requiresAuth: false }
    },
    {
      path: '/login',
      redirect: '/'
    },
    {
      path: '/change-password',
      name: 'change-password',
      component: () => import('@/views/ChangePasswordView.vue'),
      meta: { requiresAuth: true, requiresPasswordChange: false }
    },
    {
      path: '/verification',
      name: 'verification',
      component: () => import('@/views/VerificationView.vue'),
      meta: { requiresAuth: true, requiresPasswordChange: false }
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/views/DashboardView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/pilots/me',
      name: 'profile',
      component: () => import('@/views/UserProfileView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/profile',
      redirect: '/pilots/me'
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('@/views/UserManagementView.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/live',
      name: 'live-ops',
      component: () => import('@/views/LiveOpsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/flights/:id',
      name: 'flight-detail',
      component: () => import('@/views/FlightDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/fleet',
      name: 'fleet',
      component: () => import('@/views/FleetView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/fleet/new',
      name: 'aircraft-create',
      component: () => import('@/views/AircraftCreateView.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/fleet/:id/edit',
      name: 'aircraft-edit',
      component: () => import('@/views/AircraftEditView.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/fleet/:reg',
      name: 'aircraft-detail',
      component: () => import('@/views/AircraftDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/pilots',
      name: 'pilots',
      component: () => import('@/views/PilotsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/pilots/:id',
      name: 'pilot-detail',
      component: () => import('@/views/PilotDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/airports/new',
      name: 'airport-create',
      component: () => import('@/views/AirportCreateView.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/airports/:ident/edit',
      name: 'airport-edit',
      component: () => import('@/views/AirportEditView.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/airports',
      name: 'airports',
      component: () => import('@/views/AirportsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/navaids',
      name: 'navaids',
      component: () => import('@/views/NavaidsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/dataset',
      name: 'dataset',
      component: () => import('@/views/DatasetView.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/reports',
      name: 'reports',
      component: () => import('@/views/ReportsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/schedule',
      name: 'schedule',
      component: () => import('@/views/ScheduleView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/compliance',
      name: 'documents',
      component: () => import('@/views/DocumentsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/documents',
      redirect: '/compliance'
    },
    {
      path: '/anac',
      name: 'anac',
      component: () => import('@/views/AnacView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/partners',
      name: 'partners',
      component: () => import('@/views/PartnersView.vue'),
      meta: { requiresAuth: true, roles: ['administrator', 'partner'] }
    },
    {
      path: '/audit',
      name: 'audit',
      component: () => import('@/views/AuditView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/notifications',
      name: 'notifications-hub',
      component: () => import('@/views/NotificationsHubView.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/notifications/templates',
      name: 'notification-templates',
      component: () => import('@/views/NotificationTemplatesView.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/notifications/broadcasts',
      name: 'notification-broadcasts',
      component: () => import('@/views/NotificationBroadcastView.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/notifications/audit',
      name: 'notification-audit',
      component: () => import('@/views/NotificationAuditView.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/notifications/acknowledgements',
      name: 'notification-acknowledgements',
      component: () => import('@/views/NotificationAckEscalationListView.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/notifications/acknowledgements/:id',
      name: 'notification-acknowledgement-detail',
      component: () => import('@/views/NotificationAckEscalationDetailView.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/admin/console',
      name: 'admin-query-console',
      component: () => import('@/views/AdminQueryConsoleView.vue'),
      // The backend authorizes this endpoint with the canonical admin role; keep
      // this guard aligned with useAuth role aliases until route metadata is
      // migrated to canonical `admin` values.
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/views/SettingsView.vue'),
      meta: { requiresAuth: true, roles: ['administrator'] }
    },
    {
      path: '/flights',
      name: 'flights',
      component: () => import('@/views/FlightsView.vue'),
      meta: { requiresAuth: true }
    }
  ],
})

// Navigation guard for authentication
router.beforeEach(async (to) => {
  logger.navigationTo(to.path)

  await ensureAuthReady()

  if (to.meta.requiresAuth && !hasAuthenticatedSession.value) {
    logger.info(`🚫 Access denied to ${to.path} - not authenticated`)
    return { name: 'login' }
  }

  if (to.name === 'login' && hasAuthenticatedSession.value) {
    if (hasPendingVerification.value) {
      logger.info(`🛂 Redirecting authenticated user from login to verification`)
      return { name: 'verification' }
    }

    if (mustChangePassword.value) {
      logger.info(`🔒 Redirecting to change-password - password change required`)
      return { name: 'change-password' }
    }

    logger.info(`↩️ Redirecting authenticated user from login to dashboard`)
    return { name: 'dashboard' }
  }

  if (hasPendingVerification.value && hasAuthenticatedSession.value && to.name !== 'verification' && to.name !== 'change-password') {
    logger.info(`🛂 Blocking navigation to ${to.path} - verification pending`)
    return { name: 'verification' }
  }

  if (mustChangePassword.value && hasAuthenticatedSession.value && to.name !== 'change-password') {
    logger.info(`🔒 Blocking navigation to ${to.path} - password change required`)
    return { name: 'change-password' }
  }

  const requiredRoles = Array.isArray(to.meta.roles) ? to.meta.roles : []
  if (requiredRoles.length > 0 && hasAuthenticatedSession.value) {
    const authorized = requiredRoles.some((role) => hasRole(String(role)))
    if (!authorized) {
      logger.info(`🚫 Access denied to ${to.path} - missing required role(s): ${requiredRoles.join(', ')}`)
      return { name: 'dashboard' }
    }
  }
})

export default router
